import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  const lineVariants = {
    hidden: {
      pathLength: 0,
      opacity: 0
    },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          duration: 2,
          delay: 0.3 + i * 0.2,
          ease: 'easeInOut'
        },
        opacity: {
          duration: 0.3,
          delay: 0.3 + i * 0.2
        }
      }
    })
  };
  const textVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.8 + i * 0.2,
        ease: 'easeOut'
      }
    })
  };
  return <section ref={ref} className="relative min-h-screen flex items-center justify-center bg-deep-green felt-texture overflow-hidden" data-id="element-66">
      {/* Background decorative SVG lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-id="element-67">
        {/* Geometric art deco radiating lines */}
        <motion.line x1="600" y1="0" x2="600" y2="800" stroke="#daa520" strokeWidth="0.5" opacity="0.15" variants={lineVariants} custom={0} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-68" />
        <motion.line x1="0" y1="400" x2="1200" y2="400" stroke="#daa520" strokeWidth="0.5" opacity="0.15" variants={lineVariants} custom={1} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-69" />
        <motion.line x1="0" y1="0" x2="1200" y2="800" stroke="#daa520" strokeWidth="0.3" opacity="0.1" variants={lineVariants} custom={2} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-70" />
        <motion.line x1="1200" y1="0" x2="0" y2="800" stroke="#daa520" strokeWidth="0.3" opacity="0.1" variants={lineVariants} custom={3} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-71" />
        {/* Art deco chevron patterns */}
        <motion.path d="M 500 200 L 600 150 L 700 200" fill="none" stroke="#daa520" strokeWidth="0.8" opacity="0.2" variants={lineVariants} custom={2} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-72" />
        <motion.path d="M 480 220 L 600 160 L 720 220" fill="none" stroke="#daa520" strokeWidth="0.5" opacity="0.12" variants={lineVariants} custom={2.5} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-73" />
        <motion.path d="M 500 600 L 600 650 L 700 600" fill="none" stroke="#daa520" strokeWidth="0.8" opacity="0.2" variants={lineVariants} custom={3} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-74" />
        <motion.path d="M 480 580 L 600 640 L 720 580" fill="none" stroke="#daa520" strokeWidth="0.5" opacity="0.12" variants={lineVariants} custom={3.5} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-75" />
      </svg>

      {/* Corner suit icons */}
      <div className="absolute top-8 left-8 md:top-12 md:left-12 text-goldenrod/30 text-2xl md:text-3xl suit-rotate select-none" aria-hidden="true" data-id="element-76">
        ♠
      </div>
      <div className="absolute top-8 right-8 md:top-12 md:right-12 text-goldenrod/30 text-2xl md:text-3xl suit-rotate select-none" style={{
      animationDelay: '1.5s'
    }} aria-hidden="true" data-id="element-77">
        ♦
      </div>
      <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12 text-goldenrod/30 text-2xl md:text-3xl suit-rotate select-none" style={{
      animationDelay: '3s'
    }} aria-hidden="true" data-id="element-78">
        ♥
      </div>
      <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12 text-goldenrod/30 text-2xl md:text-3xl suit-rotate select-none" style={{
      animationDelay: '4.5s'
    }} aria-hidden="true" data-id="element-79">
        ♣
      </div>

      {/* Main content frame */}
      <div className="relative z-10 text-center px-6 py-20 max-w-4xl mx-auto" data-id="element-80">
        {/* Art deco frame around content */}
        <div className="relative p-8 md:p-16" data-id="element-81">
          {/* Outer frame */}
          <motion.div className="absolute inset-0 border border-goldenrod/40" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 1.2,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-82" />
          {/* Inner frame */}
          <motion.div className="absolute inset-3 md:inset-5 border border-goldenrod/20" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 1.2,
          delay: 0.5,
          ease: 'easeOut'
        }} data-id="element-83" />
          {/* Corner ornaments */}
          <motion.div className="absolute -top-1 -left-1 w-10 h-10 md:w-14 md:h-14 border-t-2 border-l-2 border-goldenrod" initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.8
        }} data-id="element-84" />
          <motion.div className="absolute -top-1 -right-1 w-10 h-10 md:w-14 md:h-14 border-t-2 border-r-2 border-goldenrod" initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.9
        }} data-id="element-85" />
          <motion.div className="absolute -bottom-1 -left-1 w-10 h-10 md:w-14 md:h-14 border-b-2 border-l-2 border-goldenrod" initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 1.0
        }} data-id="element-86" />
          <motion.div className="absolute -bottom-1 -right-1 w-10 h-10 md:w-14 md:h-14 border-b-2 border-r-2 border-goldenrod" initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 1.1
        }} data-id="element-87" />

          {/* Top decorative chevron */}
          <motion.div className="absolute -top-4 left-1/2 -translate-x-1/2 text-goldenrod text-lg tracking-[0.4em] select-none" custom={0} variants={textVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} aria-hidden="true" data-id="element-88">
            ◆ ◆ ◆
          </motion.div>

          {/* Main heading */}
          <motion.h1 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-9xl text-ivory tracking-[0.2em] md:tracking-[0.3em] mb-6" custom={0} variants={textVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-89">
            STRATEGAME
          </motion.h1>

          {/* Gold horizontal rule */}
          <motion.div className="w-32 md:w-48 h-px bg-goldenrod mx-auto mb-6" initial={{
          scaleX: 0,
          opacity: 0
        }} animate={isInView ? {
          scaleX: 1,
          opacity: 1
        } : {}} transition={{
          duration: 1,
          delay: 1.2,
          ease: 'easeOut'
        }} data-id="element-90" />

          {/* Subtitle */}
          <motion.p className="font-body text-lg md:text-xl lg:text-2xl text-goldenrod/80 italic tracking-wide" custom={2} variants={textVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-91">
            Artificial Intelligence for Strategic Mastery
          </motion.p>

          {/* Bottom decorative element */}
          <motion.div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-goldenrod text-lg tracking-[0.4em] select-none" custom={3} variants={textVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} aria-hidden="true" data-id="element-92">
            ◆ ◆ ◆
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div className="mt-16 text-goldenrod/40" initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        delay: 2.5,
        duration: 1
      }} data-id="element-93">
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-2xl select-none" aria-hidden="true" data-id="element-94">
            ♦
          </motion.div>
        </motion.div>
      </div>
    </section>;
}